import React, { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { FiFilter, FiX, FiChevronDown, FiChevronUp, FiSliders, FiFileText, FiTag } from 'react-icons/fi';
import { PiBuildings } from 'react-icons/pi';
import { FilterRow } from './filter-row';
import { ColumnFilter, FILTERABLE_COLUMNS } from './filter-types';
import { UseMultiFiltersReturn } from './use-multi-filters';
import { TagFilter } from '../../schemas/boolean_filtering';
import { TagFilterDropdown, TagOptionWithMeta } from './tag-filter-dropdown';
import { SavedSearchSelector } from './saved-search-selector';
import { FilterCard } from './filter-card';
import { ENABLE_ENTITY_TAGS } from '../../feature_flags';

export type FilterSectionType = 'requests' | 'codes' | 'entities' | 'columns';

export interface FilterPanelHandle {
  /** Expand the panel and scroll to a specific section */
  expandAndScrollTo: (section: FilterSectionType) => void;
}

interface FilterPanelProps {
  filterState: UseMultiFiltersReturn;
  onSearch?: () => void;
  /** When this changes to true, force the panel to expand */
  forceExpand?: boolean;
  /** Tag filters state (codes and document requests) */
  tagFilters: Map<string, TagFilter[]> | null;
  /** Setter for tag filters */
  setTagFilters: React.Dispatch<React.SetStateAction<Map<string, TagFilter[]> | null>>;
  /** Available codes to filter on (with current chat info) */
  codesOptions: TagOptionWithMeta[];
  /** Available document requests to filter on (with current chat info) */
  requestOptions: TagOptionWithMeta[];
  /** Available entities to filter on (with entity type info) */
  entityOptions: TagOptionWithMeta[];
  /** Callback to apply a saved search */
  onApplySavedSearch: (filters: ColumnFilter[], tagFilters: TagFilter[]) => void;
}

export const FilterPanel = forwardRef<FilterPanelHandle, FilterPanelProps>(({
  filterState,
  onSearch,
  forceExpand,
  tagFilters,
  setTagFilters,
  codesOptions,
  requestOptions,
  entityOptions,
  onApplySavedSearch,
}, ref) => {
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);

  // Refs for each filter section
  const requestsRef = useRef<HTMLDivElement>(null);
  const codesRef = useRef<HTMLDivElement>(null);
  const entitiesRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  // Expose imperative handle for scroll-to-section
  useImperativeHandle(ref, () => ({
    expandAndScrollTo: (section: FilterSectionType) => {
      // Expand panel first
      setIsFiltersExpanded(true);

      // Wait for expansion animation, then scroll
      setTimeout(() => {
        const refMap: Record<FilterSectionType, React.RefObject<HTMLDivElement>> = {
          requests: requestsRef,
          codes: codesRef,
          entities: entitiesRef,
          columns: columnsRef,
        };

        const targetRef = refMap[section];
        if (targetRef.current) {
          targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Focus the search input within the section if possible
          const input = targetRef.current.querySelector('input');
          if (input) {
            input.focus();
          }
        }
      }, 100);
    },
  }), []);

  // Expand filters when forceExpand becomes true
  useEffect(() => {
    if (forceExpand) {
      setIsFiltersExpanded(true);
    }
  }, [forceExpand]);

  const {
    filters,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    hasActiveFilters: hasActiveColumnFilters,
    activeFilterCount: activeColumnFilterCount,
  } = filterState;

  // Calculate filter counts per type
  const requestsFilterCount = tagFilters
    ? requestOptions.filter(opt => tagFilters.has(opt.id)).length
    : 0;
  const codesFilterCount = tagFilters
    ? codesOptions.filter(opt => tagFilters.has(opt.id)).length
    : 0;
  const entitiesFilterCount = tagFilters
    ? entityOptions.filter(opt => tagFilters.has(opt.id)).length
    : 0;

  // Calculate total active filters including tag filters
  const tagFilterCount = requestsFilterCount + codesFilterCount + entitiesFilterCount;
  const activeFilterCount = activeColumnFilterCount + tagFilterCount;
  const hasActiveFilters = hasActiveColumnFilters || tagFilterCount > 0;

  const handleAddFilter = useCallback((columnId?: string) => {
    addFilter(columnId);
  }, [addFilter]);

  const handleUpdateFilter = useCallback((id: string) => (updates: Partial<Omit<ColumnFilter, 'id'>>) => {
    updateFilter(id, updates);
  }, [updateFilter]);

  const handleRemoveFilter = useCallback((id: string) => () => {
    removeFilter(id);
  }, [removeFilter]);

  const handleClearAll = useCallback(() => {
    clearFilters();
    setTagFilters(null);
    setTimeout(() => onSearch?.(), 0);
  }, [clearFilters, setTagFilters, onSearch]);

  const handleSearch = useCallback(() => {
    onSearch?.();
  }, [onSearch]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  return (
    <div className="bg-white" onKeyDown={handleKeyDown}>
      {/* ========== SEARCH FILTERS SECTION ========== */}
      <div className="border-b border-[#a1a1b6]">
        {/* Filter Header */}
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <div className={`p-2 rounded-lg ${hasActiveFilters ? 'bg-[#c4b5fd]' : 'bg-[#ececf3]'}`}>
              <FiSliders className={`w-[1.125em] h-[1.125em] ${hasActiveFilters ? 'text-[#1c1c24]' : 'text-[#6d6d8a]'}`} />
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold text-[#1c1c24]">Search Filters</h3>
              <p className="text-xs text-[#6d6d8a]">Filter which documents appear in results</p>
            </div>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center rounded-full bg-[#8c8ca3] px-2.5 py-0.5 text-xs font-medium text-white">
                {activeFilterCount} active
              </span>
            )}
            {isFiltersExpanded ? (
              <FiChevronUp className="text-[#6d6d8a] w-[1.25em] h-[1.25em]" />
            ) : (
              <FiChevronDown className="text-[#6d6d8a] w-[1.25em] h-[1.25em]" />
            )}
          </button>
        </div>

        {/* Filter Content - 2-column grid layout */}
        {isFiltersExpanded && (
          <div className="px-4 pb-4 bg-[#ececf3]/50">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              {/* Document Requests Card */}
              <FilterCard
                ref={requestsRef}
                id="filter-section-requests"
                title="Document Requests"
                icon={<FiFileText className="w-4 h-4" />}
                badge={requestsFilterCount}
              >
                <TagFilterDropdown
                  options={requestOptions}
                  tagFilters={tagFilters}
                  setTagFilters={setTagFilters}
                  placeholder="Search document requests..."
                />
              </FilterCard>

              {/* Column Filters Card */}
              <FilterCard
                ref={columnsRef}
                id="filter-section-columns"
                title="Column Filters"
                icon={<FiFilter className="w-4 h-4" />}
                badge={activeColumnFilterCount}
              >
                {filters.length === 0 ? (
                  <p className="text-sm text-[#6d6d8a] mb-2">
                    No column filters added yet.
                  </p>
                ) : (
                  <div className="space-y-1.5 mb-2">
                    {filters.map(filter => (
                      <FilterRow
                        key={filter.id}
                        filter={filter}
                        onUpdate={handleUpdateFilter(filter.id)}
                        onRemove={handleRemoveFilter(filter.id)}
                      />
                    ))}
                  </div>
                )}
                <button
                  onClick={() => handleAddFilter()}
                  className="flex items-center gap-1.5 text-sm text-[#8c8ca3] hover:text-[#6d6d8a] font-medium"
                >
                  <FiFilter className="w-[0.875em] h-[0.875em]" />
                  Add column filter
                </button>
              </FilterCard>

              {/* Codes Card */}
              <FilterCard
                ref={codesRef}
                id="filter-section-codes"
                title="Codes"
                icon={<FiTag className="w-4 h-4" />}
                badge={codesFilterCount}
              >
                <TagFilterDropdown
                  options={codesOptions}
                  tagFilters={tagFilters}
                  setTagFilters={setTagFilters}
                  placeholder="Search codes..."
                />
              </FilterCard>

              {/* Entities Card (feature-flagged) */}
              {ENABLE_ENTITY_TAGS && (
                <FilterCard
                  ref={entitiesRef}
                  id="filter-section-entities"
                  title="Entities"
                  icon={<PiBuildings className="w-4 h-4" />}
                  badge={entitiesFilterCount}
                >
                  <TagFilterDropdown
                    options={entityOptions}
                    tagFilters={tagFilters}
                    setTagFilters={setTagFilters}
                    placeholder="Search entities..."
                  />
                </FilterCard>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 pt-4 border-t border-[#d5d5e0]">
              <button
                onClick={handleSearch}
                className="rounded-lg bg-[#8c8ca3] px-4 py-2 text-sm font-medium text-white hover:bg-[#7c7c95] transition-colors shadow-sm"
              >
                Apply Filters
              </button>
              {hasActiveFilters && (
                <button
                  onClick={handleClearAll}
                  className="flex items-center gap-1.5 rounded-lg border border-[#a1a1b6] bg-white px-4 py-2 text-sm font-medium text-[#1c1c24] hover:bg-[#ececf3] transition-colors"
                >
                  <FiX className="w-[0.875em] h-[0.875em]" />
                  Clear All
                </button>
              )}
              <div className="ml-auto">
                <SavedSearchSelector
                  filters={filters}
                  tagFilters={tagFilters}
                  onApplySearch={onApplySavedSearch}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

FilterPanel.displayName = 'FilterPanel';

/**
 * Get the label for a column by ID
 */
export function getColumnLabel(columnId: string): string {
  const column = FILTERABLE_COLUMNS.find(c => c.id === columnId);
  return column?.label ?? columnId;
}

export default FilterPanel;
